import { ReactNode, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Inbox,
  Loader2,
  Search,
  X,
  type LucideIcon,
} from "lucide-react";

/* =========================================================
   SHARED ADMIN PRESENTATION PRIMITIVES
========================================================= */

export const adminCardClass =
  "rounded-2xl border border-zinc-800/80 bg-zinc-900/40";

export function initialsOf(...parts: Array<string | undefined | null>) {
  return parts
    .filter(Boolean)
    .map((part) => String(part).trim().charAt(0).toUpperCase())
    .slice(0, 2)
    .join("");
}

export function formatDate(value?: string | null) {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/* ================= PAGE HEADER ================= */

export function AdminPageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
      <div className="min-w-0">
        {eyebrow && (
          <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-primary">
            {eyebrow}
          </p>
        )}

        <h1 className="mt-2 text-2xl font-black uppercase tracking-tight text-white sm:text-3xl lg:text-4xl">
          {title}
        </h1>

        {description && (
          <p className="mt-2 max-w-2xl text-sm leading-6 text-zinc-400">
            {description}
          </p>
        )}
      </div>

      {actions && (
        <div className="flex flex-wrap items-center gap-3">{actions}</div>
      )}
    </header>
  );
}

/* ================= STAT CARD ================= */

const statTones = {
  blue: "text-primary bg-primary/10 border-primary/20",
  green: "text-emerald-400 bg-emerald-400/10 border-emerald-400/20",
  amber: "text-amber-400 bg-amber-400/10 border-amber-400/20",
  red: "text-red-400 bg-red-400/10 border-red-400/20",
  zinc: "text-zinc-300 bg-zinc-800/60 border-zinc-700/60",
} as const;

export function AdminStatCard({
  label,
  value,
  hint,
  icon: Icon,
  tone = "blue",
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  icon: LucideIcon;
  tone?: keyof typeof statTones;
}) {
  return (
    <div className={`${adminCardClass} p-4 sm:p-5`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
            {label}
          </p>

          <p className="mt-2 text-2xl font-black text-white sm:text-3xl">
            {value}
          </p>

          {hint && (
            <p className="mt-1 truncate text-xs text-zinc-500">{hint}</p>
          )}
        </div>

        <span
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border ${statTones[tone]}`}
        >
          <Icon size={18} />
        </span>
      </div>
    </div>
  );
}

/* ================= TABS ================= */

export interface AdminTabItem {
  key: string;
  label: string;
  count?: number;
  icon?: LucideIcon;
}

export function AdminTabs({
  tabs,
  active,
  onChange,
}: {
  tabs: AdminTabItem[];
  active: string;
  onChange: (key: string) => void;
}) {
  return (
    <div className="-mx-1 overflow-x-auto px-1 pb-1">
      <div className="flex min-w-max gap-1 rounded-xl border border-zinc-800/80 bg-zinc-900/60 p-1">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = active === tab.key;

          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => onChange(tab.key)}
              className={`flex items-center gap-2 rounded-lg px-3 py-2 text-[11px] font-bold uppercase tracking-wider transition sm:px-4 ${
                isActive
                  ? "bg-primary text-white shadow-lg shadow-primary/20"
                  : "text-zinc-400 hover:bg-zinc-800/70 hover:text-white"
              }`}
            >
              {Icon && <Icon size={14} />}
              {tab.label}

              {typeof tab.count === "number" && (
                <span
                  className={`rounded-full px-1.5 py-0.5 text-[9px] ${
                    isActive
                      ? "bg-black/20 text-white"
                      : "bg-zinc-800 text-zinc-400"
                  }`}
                >
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ================= TOOLBAR ================= */

export function AdminToolbar({
  search,
  onSearch,
  placeholder = "Search...",
  children,
}: {
  search: string;
  onSearch: (value: string) => void;
  placeholder?: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
      <div className="relative w-full lg:max-w-sm">
        <Search
          size={15}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
        />

        <input
          value={search}
          onChange={(event) => onSearch(event.target.value)}
          placeholder={placeholder}
          className="w-full rounded-xl border border-zinc-800 bg-zinc-900/70 py-2.5 pl-9 pr-9 text-sm text-white placeholder:text-zinc-600 focus:border-primary focus:outline-none"
        />

        {search && (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => onSearch("")}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {children && (
        <div className="flex flex-wrap items-center gap-2">{children}</div>
      )}
    </div>
  );
}

export function adminSelectClass() {
  return "rounded-xl border border-zinc-800 bg-zinc-900/70 px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-zinc-300 focus:border-primary focus:outline-none";
}

/* ================= STATUS BADGE ================= */

const statusStyles: Record<string, string> = {
  active: "border-emerald-400/20 bg-emerald-400/10 text-emerald-400",
  approved: "border-emerald-400/20 bg-emerald-400/10 text-emerald-400",
  confirmed: "border-emerald-400/20 bg-emerald-400/10 text-emerald-400",
  completed: "border-sky-400/20 bg-sky-400/10 text-sky-400",
  pending: "border-amber-400/20 bg-amber-400/10 text-amber-400",
  inactive: "border-zinc-700 bg-zinc-800/60 text-zinc-400",
  rejected: "border-red-400/20 bg-red-400/10 text-red-400",
  cancelled: "border-zinc-700 bg-zinc-800/60 text-zinc-400",
  not_submitted: "border-zinc-700 bg-zinc-800/60 text-zinc-500",
};

export function AdminStatusBadge({
  status,
  label,
}: {
  status?: string | null;
  label?: string;
}) {
  const key = (status || "not_submitted").toLowerCase();
  const tone = statusStyles[key] || "border-zinc-700 bg-zinc-800/60 text-zinc-300";

  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-full border px-2.5 py-1 text-[9px] font-bold uppercase tracking-widest ${tone}`}
    >
      {label || status || "Not submitted"}
    </span>
  );
}

/* ================= EMPTY / LOADING ================= */

export function AdminEmptyState({
  title,
  description,
  icon: Icon = Inbox,
  action,
}: {
  title: string;
  description?: string;
  icon?: LucideIcon;
  action?: ReactNode;
}) {
  return (
    <div className={`${adminCardClass} flex flex-col items-center justify-center px-6 py-14 text-center`}>
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-zinc-800 bg-zinc-900 text-zinc-500">
        <Icon size={24} />
      </div>

      <h3 className="mt-4 text-sm font-bold uppercase tracking-wider text-zinc-200">
        {title}
      </h3>

      {description && (
        <p className="mt-2 max-w-md text-sm text-zinc-500">{description}</p>
      )}

      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function AdminLoader({ label = "Loading records..." }: { label?: string }) {
  return (
    <div className={`${adminCardClass} flex items-center justify-center gap-3 px-6 py-16 text-sm text-zinc-400`}>
      <Loader2 size={18} className="animate-spin text-primary" />
      {label}
    </div>
  );
}

/* ================= MODAL ================= */

export function AdminModal({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  size = "md",
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
}) {
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  const sizes = {
    sm: "max-w-sm",
    md: "max-w-lg",
    lg: "max-w-2xl",
    xl: "max-w-4xl",
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-end justify-center bg-black/75 p-0 backdrop-blur-sm sm:items-center sm:p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 24, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 24, opacity: 0, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
            onClick={(event) => event.stopPropagation()}
            className={`flex max-h-[92vh] w-full ${sizes[size]} flex-col overflow-hidden rounded-t-2xl border border-zinc-800 bg-zinc-950 shadow-2xl sm:rounded-2xl`}
          >
            <div className="flex items-start justify-between gap-4 border-b border-zinc-800 px-5 py-4">
              <div className="min-w-0">
                <h2 className="text-base font-bold text-white">{title}</h2>

                {subtitle && (
                  <p className="mt-1 text-xs text-zinc-500">{subtitle}</p>
                )}
              </div>

              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="rounded-lg p-1.5 text-zinc-500 hover:bg-zinc-900 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-5">{children}</div>

            {footer && (
              <div className="flex flex-wrap items-center justify-end gap-3 border-t border-zinc-800 px-5 py-4">
                {footer}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ================= SMALL HELPERS ================= */

export function AdminAvatar({
  src,
  name,
  size = "md",
}: {
  src?: string | null;
  name: string;
  size?: "sm" | "md" | "lg";
}) {
  const sizes = {
    sm: "h-9 w-9 text-[10px]",
    md: "h-11 w-11 text-xs",
    lg: "h-14 w-14 text-sm",
  };

  return (
    <div
      className={`${sizes[size]} flex shrink-0 items-center justify-center overflow-hidden rounded-full border border-zinc-700 bg-zinc-800 font-bold text-zinc-300`}
    >
      {src ? (
        <img
          src={src}
          alt={name}
          draggable={false}
          onContextMenu={(event) => event.preventDefault()}
          className="h-full w-full object-cover"
        />
      ) : (
        initialsOf(name) || "—"
      )}
    </div>
  );
}

export function AdminButtonPrimary({
  children,
  onClick,
  type = "button",
  disabled,
}: {
  children: ReactNode;
  onClick?: () => void;
  type?: "button" | "submit";
  disabled?: boolean;
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider text-white transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {children}
    </button>
  );
}

export function AdminButtonGhost({
  children,
  onClick,
  type = "button",
}: {
  children: ReactNode;
  onClick?: () => void;
  type?: "button" | "submit";
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      className="inline-flex items-center justify-center gap-2 rounded-xl border border-zinc-800 px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider text-zinc-300 transition hover:border-zinc-700 hover:bg-zinc-900 hover:text-white"
    >
      {children}
    </button>
  );
}

export function AdminField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
        {label}
      </span>
      {children}
    </label>
  );
}
